<template>
    <div class="weeduc-admin-page corporate-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.coursesPage.title"
                    :subtitle="t.coursesPage.subtitle"
                    :hasMargin="false"
                />
            </div>
            <div class="weeduc-admin-page__header-actions" v-if="canManage">
                <v-btn
                    color="primary"
                    variant="flat"
                    rounded="lg"
                    class="text-none"
                    @click="openCreate"
                >
                    <v-icon class="me-1" size="small">mdi-plus</v-icon>
                    {{ t.coursesPage.newLink }}
                </v-btn>
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <section class="corporate-kpi-grid" :aria-label="t.common.summary">
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.links }}</strong>
                    <span class="corporate-kpi__label">{{ t.coursesPage.totalsLinks }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.courses }}</strong>
                    <span class="corporate-kpi__label">{{ t.coursesPage.totalsCourses }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.groups }}</strong>
                    <span class="corporate-kpi__label">{{ t.coursesPage.totalsGroups }}</span>
                </article>
                <article class="corporate-kpi">
                    <strong class="corporate-kpi__value">{{ stats.mandatory }}</strong>
                    <span class="corporate-kpi__label">{{ t.coursesPage.totalsMandatory }}</span>
                </article>
            </section>

            <div class="weeduc-admin-page__panel">
                <div class="weeduc-admin-page__toolbar corporate-access-toolbar">
                    <v-text-field
                        v-model="filters.search"
                        :label="t.coursesPage.search"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        append-inner-icon="mdi-magnify"
                        @update:modelValue="reloadDebounced"
                    />
                    <v-select
                        v-model="filters.resourceType"
                        :items="resourceTypeFilterOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.coursesPage.filterType"
                        variant="outlined"
                        density="compact"
                        hide-details
                        class="weeduc-admin-v-field"
                        @update:modelValue="reload"
                    />
                    <v-select
                        v-model="filters.sectorId"
                        :items="areaFilterOptions"
                        item-title="text"
                        item-value="value"
                        :label="t.coursesPage.filterSector"
                        variant="outlined"
                        density="compact"
                        hide-details
                        clearable
                        class="weeduc-admin-v-field"
                        :loading="loadingUnits"
                        @focus="ensureUnitsLoaded"
                        @click="ensureUnitsLoaded"
                        @update:modelValue="reload"
                    />
                </div>

                <div class="weeduc-admin-page__table-section">
                    <div v-if="loading" class="corporate-placeholder">
                        <Loader :isActive="false" />
                    </div>
                    <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>
                    <div v-else-if="!rows.length" class="corporate-placeholder">
                        {{ t.coursesPage.empty }}
                    </div>
                    <div v-else class="corporate-access-list">
                        <article
                            v-for="row in rows"
                            :key="row.id"
                            class="corporate-access-card"
                        >
                            <div class="corporate-access-card__main">
                                <div
                                    class="corporate-access-card__icon"
                                    :class="row.resourceType === 'group' ? 'is-group' : 'is-course'"
                                >
                                    <fa
                                        :icon="row.resourceType === 'group' ? 'fa-layer-group' : 'fa-graduation-cap'"
                                    />
                                </div>
                                <div class="corporate-access-card__body">
                                    <div class="corporate-access-card__title-row">
                                        <h3>{{ row.resourceName || '—' }}</h3>
                                        <span
                                            class="corporate-access-chip"
                                            :class="row.resourceType === 'group' ? 'is-group' : 'is-course'"
                                        >
                                            {{
                                                row.resourceType === 'group'
                                                    ? t.coursesPage.resourceGroup
                                                    : t.coursesPage.resourceCourse
                                            }}
                                        </span>
                                        <span
                                            class="corporate-access-chip"
                                            :class="row.mandatory ? 'is-mandatory' : 'is-optional'"
                                        >
                                            {{
                                                row.mandatory
                                                    ? t.coursesPage.mandatory
                                                    : t.coursesPage.optional
                                            }}
                                        </span>
                                    </div>
                                    <div class="corporate-access-card__meta">
                                        <span>
                                            <fa icon="fa-sitemap" />
                                            {{ row.sectorName || '—' }}
                                            <template v-if="row.roleName"> · {{ row.roleName }}</template>
                                        </span>
                                        <span>
                                            <fa icon="fa-users" />
                                            {{ row.impactedUsers || 0 }} {{ t.coursesPage.impacted }}
                                        </span>
                                        <span v-if="row.deadlineDays">
                                            <fa icon="fa-calendar" />
                                            {{ row.deadlineDays }} {{ t.coursesPage.deadline }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div v-if="canManage" class="corporate-access-card__actions">
                                <v-btn
                                    size="small"
                                    variant="tonal"
                                    color="primary"
                                    rounded="lg"
                                    class="text-none"
                                    @click="openEdit(row)"
                                >
                                    {{ t.coursesPage.edit }}
                                </v-btn>
                                <v-btn
                                    size="small"
                                    variant="text"
                                    color="error"
                                    rounded="lg"
                                    class="text-none"
                                    @click="remove(row)"
                                >
                                    {{ t.coursesPage.remove }}
                                </v-btn>
                            </div>
                        </article>
                    </div>
                </div>
            </div>
        </div>

        <v-dialog v-model="dialogOpen" max-width="640" scrollable>
            <v-card :key="editingId || 'create'" rounded="lg" class="corporate-dialog">
                <v-card-title class="corporate-dialog__header">
                    <div>
                        <h2 class="corporate-dialog__title">
                            {{ editingId ? t.coursesPage.formTitleEdit : t.coursesPage.formTitle }}
                        </h2>
                        <p class="corporate-dialog__subtitle">{{ t.coursesPage.formSubtitle }}</p>
                    </div>
                </v-card-title>
                <v-card-text class="corporate-dialog__body">
                    <section class="corporate-dialog__section">
                        <p class="corporate-dialog__section-label">{{ t.common.sectionData }}</p>
                        <div class="corporate-dialog__section-body">
                            <v-select
                                v-model="form.resourceType"
                                :items="resourceTypeOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.coursesPage.resourceType"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                                @update:modelValue="onResourceTypeChange"
                            />
                            <v-autocomplete
                                v-if="form.resourceType === 'course'"
                                v-model="form.resourceIds"
                                :items="courseOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.coursesPage.selectCourses"
                                :placeholder="t.coursesPage.filterCourses"
                                :no-data-text="t.coursesPage.noCourses"
                                multiple
                                chips
                                closable-chips
                                clear-on-select
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                                :loading="loadingCourses"
                                @focus="ensureCoursesLoaded"
                                @update:modelValue="schedulePreview"
                            />
                            <v-autocomplete
                                v-else
                                v-model="form.resourceIds"
                                :items="groupOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.coursesPage.selectGroups"
                                :placeholder="t.coursesPage.filterGroups"
                                :no-data-text="t.coursesPage.noGroups"
                                multiple
                                chips
                                closable-chips
                                clear-on-select
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                                :loading="loadingGroups"
                                @focus="ensureGroupsLoaded"
                                @update:modelValue="schedulePreview"
                            />
                            <p class="corporate-dialog__hint">
                                {{
                                    editingId
                                        ? t.coursesPage.selectEditHint
                                        : t.coursesPage.selectMultipleHint
                                }}
                            </p>
                            <v-select
                                v-model="form.sectorId"
                                :items="areaOptions"
                                item-title="text"
                                item-value="value"
                                :label="t.coursesPage.sector"
                                variant="outlined"
                                density="compact"
                                class="weeduc-admin-v-field"
                                hide-details="auto"
                                :loading="loadingUnits"
                                @focus="ensureUnitsLoaded"
                                @click="ensureUnitsLoaded"
                                @update:modelValue="schedulePreview"
                            />
                            <p v-if="previewText" class="corporate-dialog__hint corporate-access-preview">
                                {{ previewText }}
                            </p>
                        </div>
                    </section>
                </v-card-text>
                <v-card-actions class="corporate-dialog__actions">
                    <v-spacer />
                    <v-btn variant="text" class="text-none" @click="dialogOpen = false">
                        {{ t.common.cancel }}
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        rounded="lg"
                        class="text-none"
                        :loading="saving"
                        @click="save"
                    >
                        {{ t.common.save }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import { corporateI18n } from '@/i18n/mixins';
import courseAssignmentsApi from '@/utils/corporate/courseAssignments';
import organizationalUnitsApi from '@/utils/corporate/organizationalUnits';
import courseGroupsApi from '@/utils/corporate/courseGroups';
import coursesApi, { unwrapCoursesList } from '@/utils/courses';
import { canManageCorporateCourses } from '@/utils/corporate/permissions';

const emptyForm = () => ({
    resourceType: 'course',
    resourceIds: [],
    sectorId: null,
    roleId: null,
    mandatory: true,
    deadlineDays: 30,
});

function isActiveCourseStatus(status) {
    if (status == null || status === '') return false;
    const normalized = String(status).trim().toLowerCase();
    return normalized === '1'
        || normalized === '2'
        || normalized === 'active'
        || normalized === 'internal'
        || normalized === 'a';
}

function isInactiveOrDeletedCourseStatus(status) {
    if (status == null || status === '') return false;
    const normalized = String(status).trim().toLowerCase();
    return ['0', '3', '4', 'i', 'inactive', 'deleted', 'blocked'].includes(normalized);
}

function isSimpleCourse(course) {
    const type = course?.type ?? course?.courseType;
    if (type == null || type === '') return true;
    return Number(type) === 1;
}

export default {
    name: 'CorporateCourseAccess',
    mixins: [corporateI18n],
    components: { TitlePage, Loader },
    data() {
        return {
            loading: false,
            saving: false,
            error: false,
            rows: [],
            units: [],
            unitsLoaded: false,
            loadingUnits: false,
            coursesAvailable: [],
            groupsAvailable: [],
            loadingCourses: false,
            loadingGroups: false,
            catalogLoading: false,
            catalogPromise: null,
            coursesLoaded: false,
            groupsLoaded: false,
            dialogOpen: false,
            editingId: null,
            form: emptyForm(),
            previewCount: null,
            searchTimeout: null,
            previewTimeout: null,
            filters: {
                search: '',
                resourceType: null,
                sectorId: null,
            },
        };
    },
    computed: {
        ...mapState(['token']),
        canManage() {
            return canManageCorporateCourses();
        },
        stats() {
            const links = this.rows.length;
            const courses = new Set(
                this.rows.filter((r) => r.resourceType === 'course').map((r) => r.resourceId),
            ).size;
            const groups = new Set(
                this.rows.filter((r) => r.resourceType === 'group').map((r) => r.resourceId),
            ).size;
            const mandatory = this.rows.filter((r) => r.mandatory).length;
            return { links, courses, groups, mandatory };
        },
        resourceTypeOptions() {
            return [
                { text: this.t.coursesPage.resourceCourse, value: 'course' },
                { text: this.t.coursesPage.resourceGroup, value: 'group' },
            ];
        },
        resourceTypeFilterOptions() {
            return [
                { text: this.t.coursesPage.all, value: null },
                ...this.resourceTypeOptions,
            ];
        },
        areaOptions() {
            return this.units
                .filter((u) => u.type === 'sector' || u.type === 'board')
                .filter((u) => u.status !== 'inactive')
                .map((u) => ({
                    text: u.type === 'board' ? `${u.name} (Diretoria)` : u.name,
                    value: u.id,
                }));
        },
        areaFilterOptions() {
            return [{ text: this.t.coursesPage.all, value: null }, ...this.areaOptions];
        },
        courseOptions() {
            return (this.coursesAvailable || []).map((c) => ({
                text: c.name || c.comercialName || `Curso ${c.id}`,
                value: String(c.id),
            }));
        },
        groupOptions() {
            return (this.groupsAvailable || []).map((g) => ({
                text: g.name || `Grupo ${g.id}`,
                value: String(g.id),
            }));
        },
        previewText() {
            if (this.previewCount == null) return '';
            return this.t.coursesPage.previewMessage.replace('{count}', String(this.previewCount));
        },
    },
    created() {
        this.bootstrap();
    },
    beforeDestroy() {
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        if (this.previewTimeout) clearTimeout(this.previewTimeout);
    },
    methods: {
        mapCatalogOption(item) {
            return {
                id: item.id,
                name: item.name || item.comercialName || item.commercialName,
            };
        },
        isSelectableCatalogStatus(status) {
            if (status == null || status === '') return true;
            if (isInactiveOrDeletedCourseStatus(status)) return false;
            const normalized = String(status).trim().toLowerCase();
            // Aceita active/1/a; se vier outro status desconhecido, ainda inclui
            if (normalized === 'active' || normalized === '1' || normalized === 'a'
                || normalized === 'internal' || normalized === '2') return true;
            return !['inactive', 'deleted', 'blocked', '0', '3', '4', 'i'].includes(normalized);
        },
        applyCatalogFromList(response) {
            if (Array.isArray(response?.coursesAvailable) && response.coursesAvailable.length) {
                this.coursesAvailable = response.coursesAvailable
                    .filter((c) => c && c.id != null)
                    .filter((c) => isSimpleCourse(c))
                    .filter((c) => this.isSelectableCatalogStatus(c.status))
                    .map(this.mapCatalogOption);
                this.coursesLoaded = true;
            }
            const groups = Array.isArray(response?.groupsAvailable) && response.groupsAvailable.length
                ? response.groupsAvailable
                : (Array.isArray(response?.content) ? response.content : []);
            if (groups.length) {
                this.groupsAvailable = groups
                    .filter((g) => g && g.id != null)
                    .filter((g) => this.isSelectableCatalogStatus(g.status))
                    .map(this.mapCatalogOption);
                this.groupsLoaded = true;
            }
        },
        /**
         * GET /corporate/course-groups → content + coursesAvailable + groupsAvailable
         */
        async loadCourseGroupsCatalog() {
            if (this.catalogLoading) return this.catalogPromise;
            this.catalogLoading = true;
            this.catalogPromise = (async () => {
                try {
                    let response;
                    try {
                        response = await courseGroupsApi.list(this.token);
                    } catch (e) {
                        console.warn('[corporate] course-groups sem status falhou, retry active', e);
                        response = await courseGroupsApi.list(this.token, { status: 'active' });
                    }
                    this.applyCatalogFromList(response);
                    return response;
                } catch (e) {
                    console.error('[corporate] falha ao carregar GET /corporate/course-groups', e);
                    throw e;
                } finally {
                    this.catalogLoading = false;
                    this.catalogPromise = null;
                }
            })();
            return this.catalogPromise;
        },
        async bootstrap() {
            this.loading = true;
            this.error = false;
            try {
                await this.reload();
            } catch (e) {
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        async ensureUnitsLoaded() {
            if (this.unitsLoaded || this.loadingUnits) return;
            this.loadingUnits = true;
            try {
                const response = await organizationalUnitsApi.list(this.token);
                this.units = response.content || [];
                this.unitsLoaded = true;
            } catch (e) {
                console.warn('[corporate] falha ao carregar unidades organizacionais', e);
                this.units = [];
            } finally {
                this.loadingUnits = false;
            }
        },
        async ensureCoursesLoaded() {
            if (this.coursesLoaded || this.loadingCourses) return;
            this.loadingCourses = true;
            try {
                // Preferência: catálogo corporativo (GET /corporate/course-groups → coursesAvailable)
                try {
                    await this.loadCourseGroupsCatalog();
                } catch (e) {
                    // continua para fallback
                }
                if (this.coursesAvailable.length) {
                    this.coursesLoaded = true;
                    return;
                }
                // Fallback legado: GET /course ({ courses, groupCourses })
                const data = await coursesApi.getDataCourse(this.token);
                this.coursesAvailable = unwrapCoursesList(data)
                    .filter((course) => course && course.id != null)
                    .filter((course) => isSimpleCourse(course))
                    .filter((course) => isActiveCourseStatus(course.status))
                    .filter((course) => !isInactiveOrDeletedCourseStatus(course.status))
                    .map((course) => ({
                        id: course.id,
                        name: course.comercialName || course.commercialName || course.name,
                        dayExpired: course.dayExpired ?? course.day_expired ?? null,
                    }));
                this.coursesLoaded = true;
            } catch (e) {
                console.warn('[corporate] falha ao carregar cursos', e);
                this.coursesAvailable = [];
                this.coursesLoaded = true;
            } finally {
                this.loadingCourses = false;
            }
        },
        async ensureGroupsLoaded() {
            if (this.groupsLoaded || this.loadingGroups) return;
            this.loadingGroups = true;
            try {
                // GET /corporate/course-groups (pacote de grupo / type=2)
                await this.loadCourseGroupsCatalog();
                if (!this.groupsAvailable.length) {
                    // Retry explícito só com content
                    const response = await courseGroupsApi.list(this.token);
                    this.groupsAvailable = (response.content || response.groupsAvailable || [])
                        .filter((group) => group && group.id != null)
                        .filter((group) => this.isSelectableCatalogStatus(group.status))
                        .map(this.mapCatalogOption);
                }
                this.groupsLoaded = true;
            } catch (e) {
                console.error('[corporate] falha ao carregar grupos/trilhas', e);
                this.groupsAvailable = [];
                this.groupsLoaded = true;
            } finally {
                this.loadingGroups = false;
            }
        },
        reloadDebounced() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.reload(), 300);
        },
        async reload() {
            try {
                const params = {
                    page: 0,
                    size: 200,
                };
                if (this.filters.search) params.search = this.filters.search;
                if (this.filters.resourceType) params.resourceType = this.filters.resourceType;
                if (this.filters.sectorId) params.sectorId = this.filters.sectorId;
                const response = await courseAssignmentsApi.list(this.token, params);
                this.rows = Array.isArray(response?.content) ? response.content : [];
                this.applyCatalogFromList(response);
            } catch (e) {
                console.error('[corporate] falha ao listar liberações', e);
                this.error = true;
                this.rows = [];
            }
        },
        async openCreate() {
            this.editingId = null;
            this.form = emptyForm();
            this.previewCount = null;
            this.dialogOpen = true;
            await Promise.all([
                this.ensureCoursesLoaded(),
                this.ensureGroupsLoaded(),
                this.ensureUnitsLoaded(),
            ]);
        },
        async openEdit(row) {
            this.editingId = row.id;
            this.form = {
                resourceType: row.resourceType || 'course',
                resourceIds: [String(row.resourceId)],
                sectorId: row.sectorId,
                roleId: null,
                mandatory: row.mandatory === true,
                deadlineDays: row.deadlineDays ?? null,
            };
            this.dialogOpen = true;
            const resourceLoad = this.form.resourceType === 'course'
                ? this.ensureCoursesLoaded()
                : this.ensureGroupsLoaded();
            await Promise.all([resourceLoad, this.ensureUnitsLoaded()]);
            this.ensureSelectedResourceInOptions(row);
            this.schedulePreview();
        },
        ensureSelectedResourceInOptions(row) {
            const selectedId = String(row.resourceId || '');
            if (!selectedId) return;

            if (this.form.resourceType === 'course') {
                const exists = (this.coursesAvailable || []).some(
                    (c) => String(c.id) === selectedId
                );
                if (!exists) {
                    this.coursesAvailable = [
                        ...(this.coursesAvailable || []),
                        {
                            id: selectedId,
                            name: row.resourceName || selectedId,
                        },
                    ];
                }
                return;
            }

            const exists = (this.groupsAvailable || []).some(
                (g) => String(g.id) === selectedId
            );
            if (!exists) {
                this.groupsAvailable = [
                    ...(this.groupsAvailable || []),
                    {
                        id: selectedId,
                        name: row.resourceName || selectedId,
                    },
                ];
            }
        },
        async onResourceTypeChange() {
            this.form.resourceIds = [];
            this.previewCount = null;
            if (this.form.resourceType === 'course') {
                await this.ensureCoursesLoaded();
            } else {
                await this.ensureGroupsLoaded();
            }
        },
        selectedResourceIds() {
            const raw = this.form.resourceIds;
            if (Array.isArray(raw)) {
                return raw.map(String).filter(Boolean);
            }
            return raw ? [String(raw)] : [];
        },
        schedulePreview() {
            clearTimeout(this.previewTimeout);
            this.previewTimeout = setTimeout(() => this.refreshPreview(), 400);
        },
        async refreshPreview() {
            const resourceIds = this.selectedResourceIds();
            if (!this.form.sectorId || !resourceIds.length) {
                this.previewCount = null;
                return;
            }
            try {
                const response = await courseAssignmentsApi.preview(this.token, {
                    resourceType: this.form.resourceType,
                    resourceId: resourceIds[0],
                    sectorId: this.form.sectorId,
                    roleId: null,
                    mandatory: this.form.mandatory === true,
                });
                this.previewCount = response.impactedUsers ?? 0;
            } catch (e) {
                this.previewCount = null;
            }
        },
        async save() {
            const resourceIds = this.selectedResourceIds();
            if (!resourceIds.length) {
                alert(this.t.coursesPage.resourceRequired);
                return;
            }
            if (!this.form.sectorId) {
                alert(this.t.coursesPage.sectorRequired);
                return;
            }

            const isSameKey = (row, resourceId) => (
                String(row.resourceType) === String(this.form.resourceType)
                && String(row.resourceId) === String(resourceId)
                && String(row.sectorId) === String(this.form.sectorId)
            );

            const duplicates = resourceIds.filter((resourceId) => this.rows.some((row) => {
                if (this.editingId && String(row.id) === String(this.editingId)) return false;
                return isSameKey(row, resourceId);
            }));
            if (duplicates.length) {
                alert(this.t.coursesPage.duplicateAssignment);
                return;
            }

            this.saving = true;
            try {
                const basePayload = {
                    resourceType: this.form.resourceType,
                    sectorId: this.form.sectorId,
                    roleId: null,
                    mandatory: Boolean(this.form.mandatory),
                    deadlineDays: this.form.deadlineDays || null,
                };
                if (this.editingId) {
                    await courseAssignmentsApi.update(this.editingId, {
                        ...basePayload,
                        resourceId: resourceIds[0],
                    }, this.token);
                    for (const resourceId of resourceIds.slice(1)) {
                        await courseAssignmentsApi.create(this.token, {
                            ...basePayload,
                            resourceId,
                        });
                    }
                } else {
                    for (const resourceId of resourceIds) {
                        await courseAssignmentsApi.create(this.token, {
                            ...basePayload,
                            resourceId,
                        });
                    }
                }
                this.dialogOpen = false;
                await this.reload();
            } catch (e) {
                const status = e?.response?.status;
                const apiMessage = e?.response?.data?.message;
                if (status === 409) {
                    alert(this.t.coursesPage.duplicateAssignment);
                } else {
                    alert(apiMessage || e.message || this.t.common.error);
                }
            } finally {
                this.saving = false;
            }
        },
        async remove(row) {
            if (!window.confirm(this.t.coursesPage.removeConfirm)) return;
            try {
                await courseAssignmentsApi.remove(this.token, row.id);
                await this.reload();
            } catch (e) {
                alert(this.t.common.error);
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-access-toolbar {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
}

@media (min-width: 960px) {
    .corporate-access-toolbar {
        grid-template-columns: minmax(180px, 1.4fr) minmax(140px, 1fr) minmax(140px, 1fr) minmax(140px, 1fr);
        align-items: end;
    }
}

.corporate-access-list {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.corporate-access-card {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border: 1px solid #e8eaf0;
    border-radius: 14px;
    background: #fff;
}

.corporate-access-card__main {
    display: flex;
    gap: 0.9rem;
    min-width: 0;
    flex: 1;
}

.corporate-access-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: #eef1fb;
    color: #3b3f8c;
}

.corporate-access-card__icon.is-group {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-card__title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
    margin-bottom: 0.4rem;
}

.corporate-access-card__title-row h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 700;
    color: #1a1d26;
}

.corporate-access-chip {
    display: inline-flex;
    align-items: center;
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 700;
}

.corporate-access-chip.is-course {
    background: #eef1fb;
    color: #3b3f8c;
}

.corporate-access-chip.is-group {
    background: #e8f5f0;
    color: #1f7a57;
}

.corporate-access-chip.is-mandatory {
    background: #fff4e5;
    color: #b76e00;
}

.corporate-access-chip.is-optional {
    background: #f0f2f5;
    color: #5c6370;
}

.corporate-access-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem 1.1rem;
    font-size: 0.82rem;
    color: #5c6370;
}

.corporate-access-card__meta span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
}

.corporate-access-card__actions {
    display: flex;
    align-items: center;
    gap: 0.35rem;
}

.corporate-access-preview {
    margin-top: 0.25rem !important;
    font-weight: 600;
    color: #3b3f8c !important;
}

.dashboard.dark .corporate-access-card,
.dark .corporate-access-card {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-access-card__title-row h3,
.dark .corporate-access-card__title-row h3 {
    color: #f1f3f7;
}

.dashboard.dark .corporate-access-card__icon,
.dark .corporate-access-card__icon {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-access-card__icon.is-group,
.dark .corporate-access-card__icon.is-group {
    background: rgba(31, 122, 87, 0.2);
    color: #6ee7b7;
}

.dashboard.dark .corporate-access-chip,
.dark .corporate-access-chip {
    background: rgba(165, 180, 252, 0.15);
    color: #c4b5fd;
}

.dashboard.dark .corporate-access-card__meta,
.dark .corporate-access-card__meta {
    color: #9ca3b4;
}

.dashboard.dark .corporate-access-preview,
.dark .corporate-access-preview {
    color: #c4b5fd !important;
}

.dashboard.dark .corporate-access-card__actions :deep(.v-btn--variant-text),
.dark .corporate-access-card__actions :deep(.v-btn--variant-text) {
    color: #c4b5fd !important;
}
</style>
